import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, ClipboardCheck, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SortHeaderButton } from "@/components/ui/sort-header-button";
import { SortScopeNote } from "@/components/ui/sort-scope-note";
import { useTableControls } from "@/lib/use-table-controls";
import { dateSortValue } from "@/lib/table-sort";
import { orgTodayKey } from "@/lib/datetime";

/**
 * CONFIRM THE DEVICE BEFORE YOU TEST IT (#1453) — the office half.
 *
 * Every backflow main nobody has confirmed on site, every DCDA/RPDA with no
 * bypass linked (and no reason there isn't one), and every device a tech
 * excused with "No tag / not legible" or "there's no bypass" — sorted by the
 * next scheduled visit, so the office can see what next week's jobs will
 * ask the techs, and resolve the exceptions from the tag photo or the last
 * city filing. Nothing here is new data: "never confirmed" is a NULL stamp,
 * "no bypass" is a detector with no child row — the two checks the app had
 * never run.
 */

interface UnconfirmedRow {
    id: string;
    name: string | null;
    assemblyType: string | null;
    manufacturer: string | null;
    model: string | null;
    serialNumber: string | null;
    sizeLabel: string | null;
    serviceType: string | null;
    siteId: string;
    siteName: string | null;
    city: string | null;
    customerName: string | null;
    cameFrom: "import" | "groundwork";
    nextVisitAt: string | null;
    nextVisitJobId: string | null;
    identityConfirmedAt: string | null;
    notLegibleFields: string[];
    bypassMissingReason: string | null;
    needsVerificationReason: string | null;
    isDetector: boolean;
    hasBypassChild: boolean;
    gaps: string[];
}

interface UnconfirmedResponse {
    counts: {
        never_confirmed: number;
        no_bypass: number;
        not_legible: number;
        bypass_missing: number;
        serves_blank: number;
        total: number;
    };
    rows: UnconfirmedRow[];
}

const GAP_LABELS: Record<string, string> = {
    assembly_type: "type blank",
    size_label: "size blank",
    manufacturer: "make blank",
    model: "model blank",
    serial_number: "serial blank",
    service_type: "serves blank",
    location_description: "location blank",
    bypass: "no bypass linked",
    identity_confirmed: "never confirmed",
};

const NOT_LEGIBLE_LABELS: Record<string, string> = {
    manufacturer: "make",
    model: "model",
    serial_number: "serial",
};

function deviceLabel(r: UnconfirmedRow): string {
    const head = [r.sizeLabel, (r.assemblyType ?? "").toUpperCase(), r.manufacturer, r.model].filter(Boolean).join(" ");
    return head || r.name || "Device";
}

function visitLabel(iso: string | null): string {
    if (!iso) return "not scheduled";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "not scheduled";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "numeric", day: "numeric" });
}

/** What the office sees in the "what's missing" column, as chips. */
export function rowChips(r: UnconfirmedRow): Array<{ text: string; tone: "bad" | "warn" | "plain" | "good" }> {
    const chips: Array<{ text: string; tone: "bad" | "warn" | "plain" | "good" }> = [];
    for (const g of r.gaps) {
        if (g === "identity_confirmed") chips.push({ text: "never confirmed", tone: "bad" });
        else if (g === "bypass") chips.push({ text: "no bypass linked", tone: "warn" });
        else chips.push({ text: GAP_LABELS[g] ?? g, tone: g === "service_type" ? "warn" : "bad" });
    }
    if (r.notLegibleFields.length > 0) {
        chips.push({
            text: `${r.notLegibleFields.map((f) => NOT_LEGIBLE_LABELS[f] ?? f).join(" + ")} "not legible" · tag photo on the job`,
            tone: "plain",
        });
    }
    if (r.bypassMissingReason) {
        chips.push({ text: `tech said "no bypass — ${r.bypassMissingReason}"`, tone: "good" });
    }
    if (r.needsVerificationReason && !r.gaps.includes("identity_confirmed")) {
        chips.push({ text: r.needsVerificationReason, tone: "warn" });
    }
    return chips;
}

function csv(rows: UnconfirmedRow[]): string {
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = [
        "Next visit",
        "Customer",
        "Location",
        "City",
        "Device",
        "Serial",
        "Serves",
        "What's missing",
        "Came from",
        "Asset id",
    ];
    const lines = rows.map((r) =>
        [
            r.nextVisitAt ?? "",
            r.customerName,
            r.siteName,
            r.city,
            deviceLabel(r),
            r.serialNumber,
            r.serviceType,
            rowChips(r)
                .map((c) => c.text)
                .join("; "),
            r.cameFrom === "import" ? "ServiceTitan import" : "Groundwork",
            r.id,
        ]
            .map(esc)
            .join(","),
    );
    return [head.map(esc).join(","), ...lines].join("\n");
}

const UNCONFIRMED_SORT: Record<string, (r: UnconfirmedRow) => string | number | null> = {
    nextVisit: (r) => dateSortValue(r.nextVisitAt),
    customer: (r) => r.customerName,
    device: (r) => deviceLabel(r),
    // By how many gaps the device has; the first click puts the worst on top.
    missing: (r) => r.gaps.length,
    cameFrom: (r) => (r.cameFrom === "import" ? "ServiceTitan import" : "Groundwork"),
};

const TONE: Record<"bad" | "warn" | "plain" | "good", string> = {
    bad: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
    warn: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    plain: "bg-muted text-foreground/70",
    good: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
};

export function UnconfirmedDevicesCard() {
    const [expanded, setExpanded] = useState(false);
    const q = useQuery({
        queryKey: ["assets", "unconfirmed"],
        queryFn: () => api.get<UnconfirmedResponse>("/api/asset-identity/unconfirmed?limit=300"),
        refetchInterval: 120_000,
    });
    const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
    // Sort all loaded rows before the 8-row preview cuts them down. Until
    // someone clicks a header, the server's next-visit order stands.
    const { visible, sortKey, sortDir, toggleSort } = useTableControls(rows, {
        sortId: "office-review.unconfirmed-devices",
        sortAccessors: UNCONFIRMED_SORT,
        descFirst: ["missing"],
    });
    if (!q.data || q.data.counts.total === 0) return null;
    const { counts } = q.data;
    const shown = expanded ? visible : visible.slice(0, 8);
    const sortHeader = (key: string, label: string) => (
        <SortHeaderButton
            label={label}
            active={sortKey === key}
            dir={sortDir}
            onClick={() => toggleSort(key)}
            className="uppercase tracking-wide"
        />
    );

    const download = () => {
        const blob = new Blob([csv(rows)], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `devices-not-yet-confirmed-${orgTodayKey()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="rounded-lg border bg-card p-4 space-y-3" data-testid="unconfirmed-devices-card">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h3 className="text-base font-semibold flex items-center gap-2">
                        <ClipboardCheck className="h-4 w-4" />
                        Devices not yet confirmed in the field
                    </h3>
                    <p className="text-xs text-foreground/70 mt-0.5">
                        Devices we inherited but nobody has confirmed on site, plus detector mains with no bypass
                        linked. The tech app asks on the next visit; the exceptions ("not legible", "no bypass") are
                        yours to settle.
                    </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={download} className="gap-1.5">
                    <Download className="h-3.5 w-3.5" /> CSV
                </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 text-xs">
                <span className={cn("rounded-full px-2.5 py-0.5 font-semibold", TONE.bad)}>
                    {counts.never_confirmed.toLocaleString()} never confirmed
                </span>
                <span className={cn("rounded-full px-2.5 py-0.5 font-semibold", TONE.warn)}>
                    {counts.no_bypass.toLocaleString()} DCDA/RPDA with no bypass linked
                </span>
                <span className={cn("rounded-full px-2.5 py-0.5 font-semibold", TONE.warn)}>
                    {counts.serves_blank.toLocaleString()} serves not on file
                </span>
                <span className={cn("rounded-full px-2.5 py-0.5 font-semibold", TONE.plain)}>
                    {counts.not_legible.toLocaleString()} "not legible" · {counts.bypass_missing.toLocaleString()} "no
                    bypass"
                </span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-[10.5px] uppercase tracking-wide text-foreground/60">
                            <th className="py-1.5 pr-2">{sortHeader("nextVisit", "Next visit")}</th>
                            <th className="py-1.5 pr-2">{sortHeader("customer", "Customer · location")}</th>
                            <th className="py-1.5 pr-2">{sortHeader("device", "Device")}</th>
                            <th className="py-1.5 pr-2">{sortHeader("missing", "What's missing")}</th>
                            <th className="py-1.5 pr-2">{sortHeader("cameFrom", "Came from")}</th>
                            <th className="py-1.5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((r) => (
                            <tr key={r.id} className="border-t align-top">
                                <td className="py-2 pr-2 whitespace-nowrap">
                                    {r.nextVisitJobId ? (
                                        <Link
                                            to={`/jobs/${r.nextVisitJobId}`}
                                            className="underline-offset-2 hover:underline"
                                        >
                                            {visitLabel(r.nextVisitAt)}
                                        </Link>
                                    ) : (
                                        <span className="text-foreground/60">{visitLabel(r.nextVisitAt)}</span>
                                    )}
                                </td>
                                <td className="py-2 pr-2">
                                    <div className="font-medium">{r.customerName ?? "—"}</div>
                                    <Link
                                        to={`/sites/${r.siteId}`}
                                        className="text-foreground/70 underline-offset-2 hover:underline"
                                    >
                                        {[r.siteName, r.city].filter(Boolean).join(" · ") || "Location"}
                                    </Link>
                                </td>
                                <td className="py-2 pr-2">
                                    <div className="font-medium">{deviceLabel(r)}</div>
                                    <div className="text-foreground/70">
                                        {r.serialNumber ? `serial ${r.serialNumber}` : "no serial"}
                                    </div>
                                </td>
                                <td className="py-2 pr-2">
                                    <div className="flex flex-wrap gap-1">
                                        {rowChips(r).map((c, i) => (
                                            <span
                                                key={i}
                                                className={cn("rounded-full px-2 py-0.5 font-semibold", TONE[c.tone])}
                                            >
                                                {c.text}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="py-2 pr-2 whitespace-nowrap text-foreground/70">
                                    {r.cameFrom === "import" ? "ServiceTitan import" : "Groundwork"}
                                </td>
                                <td className="py-2 whitespace-nowrap">
                                    <Button
                                        asChild
                                        size="sm"
                                        variant={
                                            r.isDetector && !r.hasBypassChild && !r.bypassMissingReason
                                                ? "default"
                                                : "outline"
                                        }
                                        className="h-7 text-xs"
                                    >
                                        <Link to={`/assets/${r.id}`}>
                                            {r.isDetector && !r.hasBypassChild && !r.bypassMissingReason
                                                ? "Link bypass"
                                                : r.notLegibleFields.length > 0
                                                  ? "Read tag"
                                                  : r.bypassMissingReason
                                                    ? "Decide"
                                                    : "Edit"}
                                        </Link>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <SortScopeNote
                loaded={rows.length}
                total={counts.total}
                noun="devices"
                which="the soonest visits"
                hint=""
            />
            {rows.length > 8 && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded((v) => !v)}
                    className="gap-1"
                >
                    {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {expanded
                        ? "Show fewer"
                        : `Show all ${rows.length}${counts.total > rows.length ? ` of ${counts.total.toLocaleString()}` : ""}`}
                </Button>
            )}
        </div>
    );
}
